<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter" id="sec-18">
<h1 class="chapter" id="pAnU">
					BIBLICAL CHANGE AMONG CHURCHES OF CHRIST </h1>
<h2 id="pAqx"><b>
					Richard Hughes</b> </h2>
<br/> <br/>
<p id="pz18-2" style="text-indent: 0">
					Without intending to be so, this book is a tribute to D’Esta Love and the twenty-eight
					other women whose sermons grace these pages. It is a tribute to them because, by
					persisting in their careful reading of the biblical text, they learned that the gospel
					is inclusive, not exclusive. It is a tribute to them because they refused to bow
					to entrenched attempts to segregate the church by gender. And it is a tribute to
					them because of their courage, for in pulpits of Churches of Christ across this land
					and beyond they have shared with their brothers and sisters the glorious gospel message,
					often against overwhelming odds. And now they have shared these sermons with us,
					their readers. </p>
<p id="pz18-3">
					This book is also a tribute to the Church of Christ and the commitment of its people
					to the biblical text. From the earliest days of our movement in the nineteenth century,
					we have always said that we are committed to no ideology or theory about the Bible,
					but to the biblical text itself. In 1830, for example, the Church of Christ at Concord,
					Kentucky, issued a statement that summarizes a conviction that has characterized
					Churches of Christ from that day to this: “The <i>fatal error</i> of all reformers,” that
					church announced, “has been that they have too hastily concluded <i>that they knew the
					whole truth</i>. . . . We have no reason to conclude, we know all the truth. . . . We
					have nothing to lose in this inquiry after truth. We have no system to bind us to
					human opinion.” </p>
<p id="pz18-4">
					And that is the spirit that has encouraged many in Churches of Christ to rethink
					what the Bible teaches about the role of women in the public worship of the church.
					</p>
<p id="pz18-5">
					When I served as the editor of <i>Mission Journal</i> in the late 1970s, I received a handwritten
					letter from a woman lamenting the fact that neither <span id="z19-1">she nor any of the other women
					in her congregation were permitted to make any meaningful contributions to the teaching
					or the public worship of that church. They could not lead in pubic prayer. They could
					not serve communion. They could not read Scripture aloud. They could not preach.
					And they could not teach others publicly when men—even boys—were present. </span></p>
<p id="pz19-2">
					She grieved all the more since she knew that she was far better equipped to teach
					the Bible than were many of the men in that congregation. None of the men—including
					the elders—had gone to college and some had not finished high school. But they were
					men and were therefore privileged above the women to teach the truths of the Christian
					faith, qualified mostly by their gender. </p>
<p id="pz19-3">
					On the other hand, this woman held a college degree and, with the tools she had acquired
					in college, had spent years studying the biblical text. She was fully equipped to
					teach, even to preach. But she could not speak a word of instruction or admonition
					in the public worship simply because of her gender. </p>
<p id="pz19-4">
					This woman’s letter to me was neither bitter nor strident, but it was forceful and
					did not mince words. I assumed she was a younger woman whose perspectives had been
					shaped, in part, by the spirit of gender equality so prevalent at that time. </p>
<p id="pz19-5">
					But then, after several paragraphs explaining the reasons for her discouragement,
					all penned in a beautiful hand, wild marks suddenly appeared on the paper. The marks
					went up and down and across and diagonally with no rhyme or reason. I thought, <i>This
					woman has become so angry that she has lost her senses in a fit of rage.</i> </p>
<p id="pz19-6">
					But when I turned the letter over, I made a startling discovery: “Please accept my
					apology,” she wrote, “on behalf of my grandchild who marked up my letter with crayons
					when I was out of the room.” </p>
<p id="pz19-7">
					And then I knew. This was not a young woman at all, but an older woman—a grandmother,
					in fact—who had been struggling with the silence imposed on her for decades. And
					the story she told symbolized the plight of thousands upon thousands of women in
					Churches of Christ, many with enormous gifts in biblical teaching and understanding,
					whose voices <span id="z20-1">have been similarly muted over some two hundred years of the history
					of our tradition. </span></p>
<p id="pAq8">
					While this woman wrote to lament the fact that her voice had been silenced, there
					have been other women in Churches of Christ who have silenced their own voices. These
					women have firmly believed that Scripture requires their silence in the assembly
					of the church when men are present. </p>
<p id="pAd_">
					The New Testament scholar David Balch grew up on a farm outside of Pampa, Texas,
					in the 1940s and 1950s. His parents—Elmer and Claudine Balch—were in many ways pillars
					of the Church of Christ in that community. His mother, he told me, was the sister
					of a noted chemistry professor in a Church of Christ-related college. “She was a
					brilliant woman,” he said. <a id="pAYF">
					“Her mind was as good as [her brother’s].” And then, with pathos in his voice and
					obvious pain in his heart, he recalled, “She never said a word in church her whole
					ninety years.” </a></p><a id="pAYF">
</a><p id="pz20-4"><a id="pAYF">
					His mother’s silence impacted David so profoundly that, as a doctoral student in
					New Testament studies at Yale, he focused much of his academic work on what the Bible
					actually taught regarding the role of women in the worship of the assembled church.
					In pursuing this topic, David was an authentic child of Churches of Christ, seeking
					to understand the biblical text ever more clearly. And when he determined—at least
					to his own satisfaction—that Churches of Christ had misconstrued the Bible’s teaching
					on that topic, he sought to convince his mother, but to no avail. David carries the
					burden of her silence to this day.<sup></sup></a><a href="#/#pz22-10" id="pz22-10a">1</a> </p>
<p id="pz20-5">
					But a happier story emerges from the Church of Christ in Midlothian, Texas. As most
					readers of this book will undoubtedly know, Jack and LeMoine Lewis (Jack at the Harding
					School of Theology and LeMoine at Abilene Christian University) have inspired scores
					of men—and, indeed, some women—to take up the mantle of scholarship in biblical and
					related studies. And those individuals have, in turn, inspired literally hundreds
					of men—and, indeed, some women—to respond to the call of ministry in a local church.
					And LeMoine, by directing the Bible Teachers Workshop at </p>
<p id="pz20-6" style="text-indent: 0">
					Abilene Christian from 1959 to 1966, inspired hundreds, perhaps thousands, <span id="z21-1">of dedicated
					Christian men and women to become better teachers of the biblical text. </span></p>
<p id="pz21-2">
					LeMoine and Jack grew up in the Church of Christ in Midlothian, a small farming community
					about thirty miles south of Dallas and Fort Worth. In an interview with Jack, he
					told me that people with education—any level of education at all—were few and far
					between in that congregation.<sup><a href="#/#pz22-11" id="pz22-11a">2</a></sup> But according to LeMoine’s daughter, Claudia Lewis
					Randolph, </p>
<blockquote id="pz21-3">
<p class="p">
						There was one person in Midlothian who had done much in the way of education—the
						town librarian who held a master’s degree. Dad, when discussing the role of women,
						would point back to the Midlothian church. The librarian, a woman, taught the adult
						Bible class when he was growing up. The elders’ explanation to any who complained
						was, “She isn’t usurping authority. We asked her to teach. She is by far the most
						qualified.” </p>
</blockquote>
<p id="pz21-4" style="text-indent: 0">
					Claudia concluded, “Scholarship may be tended, watered, and fertilized at a university
					such as ACU, but most of the time the seeds were planted in some church by some Bible
					school teacher or some elder who took the time to learn and then teach the word.”
					And then she said, “I think if my Dad were . . . asked about scholarship, he would
					point back to the local church, its elders and its Bible school teachers,” and especially
					perhaps to the woman who taught the adults when Jack and LeMoine grew up in Midlothian,
					Texas.<sup><a href="#/#pz22-12" id="pz22-12a">3</a></sup> </p>
<p id="pz21-5">
					Based on that story, we are left to speculate on whether there might have been a
					LeMoine Lewis or a Jack Lewis had it not been for the woman who was their first serious
					teacher of the biblical text. And given the pervasive influence Jack and LeMoine
					exerted on scholarship and ministry in Churches of Christ, that question, in turn,
					leads us to wonder if there would have been an Abraham Malherbe, an Everett Ferguson,
					a Kathy Pulley, a D’Esta Love, a Robert M. Randolph, or a host of other great preachers
					and teachers of the biblical text had it not been for this talented and godly woman
					who first inspired both Jack and LeMoine in Bible classes at the <span id="z22-1">Church of Christ
					in the small farming community of Midlothian, Texas, so many years ago. </span></p>
<p id="pz22-2">
					None of these stories, of course, have any bearing on the long-standing discussion
					within Churches of Christ regarding the role of women in the public worship of the
					church and especially in the pulpit. To resolve that debate, we have always turned—and
					I trust will always turn—to the biblical text. </p>
<p id="pz22-3">
					But one thing is certain—the women whose sermons appear in this book compel us to
					stand with that church in Concord, Kentucky, whose members affirmed almost two hundred
					years ago, “We have nothing to lose in this inquiry after truth. We have no system
					to bind us to human opinion.” And they compel us to explore again the meaning of
					that great biblical principle upon which the work of these women must finally rest:
					</p>
<p id="pz22-4" style="text-indent: 0">
					“There is neither Jew nor Greek, there is neither slave nor free, there is neither
					male nor female; for you are all one in Christ Jesus.” </p>
<br/>
<p id="pz22-6" style="text-indent: 0;margin-bottom: 1em;">
<b>Jan Hughes</b> is a writer and speaker and for eight years co-taught with her husband,
					Richard, an honors first-year seminar at Messiah College on the theme, “Learning
					to Tell Our Stories” </p>
<p id="pz22-7" style="text-indent: 0">
<b>Richard Hughes</b> is Distinguished Professor of Religion Emeritus at both Pepperdine
					University and Messiah College </p>
<br/>
<p class="center" id="pz22-9" style="font-size:1.1em;">
					Notes </p>
<p id="pz22-10">
<sup><a href="#/#pz22-10a">1</a></sup>David Balch, interview with Richard Hughes, Pacific Lutheran Theological Seminary,
						Berkeley, California, March 28, 2014. </p>
<p id="pz22-11">
<sup><a href="#/#pz22-11a">2</a></sup>Jack Lewis, interview with Richard Hughes, Memphis, Tennessee, February 10, 2014.
						</p>
<p id="pz22-12">
<sup><a href="#/#pz22-12a">3</a></sup>Claudia Lewis Randolph, letter to Richard Hughes, January 26, 2015. </p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter01.vue?277ab60b